import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccountOptionsView, CardView, OtpStepView, ScanTokenResponse, SessionGrant, WalletPassRequestResult, WalletPassStatusView, WalletProviderKey } from '@ulp/consumer-core';
import { apiFetch } from './api';
import { useSession } from './session';
import { useTenant } from './tenant';

export { hasCapability, isPhase1, preferredWallets } from './phase1-rules';

function useKey() {
  const { slug } = useTenant();
  const { memberId } = useSession();
  return (...parts: string[]) => ['p1', slug, memberId ?? 'anon', ...parts];
}

export function useCard() {
  const key = useKey();
  const { signedIn } = useSession();
  return useQuery({ queryKey: key('card'), queryFn: () => apiFetch<CardView>('/app/me/card'), enabled: signedIn, staleTime: 5 * 60_000 });
}

/** Wallet durumu; kart PENDING iken 4 sn'de bir yenilenir ("Kartınız hazırlanıyor"). */
export function useWalletPasses() {
  const key = useKey();
  const { signedIn } = useSession();
  return useQuery({
    queryKey: key('wallet-pass'),
    queryFn: () => apiFetch<WalletPassStatusView>('/app/me/wallet-pass'),
    enabled: signedIn,
    refetchInterval: (q) => (q.state.data?.cards.some((c) => c.status === 'PENDING') ? 4_000 : false),
  });
}

export function useAddToWallet() {
  const qc = useQueryClient();
  const key = useKey();
  return useMutation({
    mutationFn: (provider: WalletProviderKey) => apiFetch<WalletPassRequestResult>(`/app/me/wallet-pass/${provider.toLowerCase()}`, { method: 'POST', timeoutMs: 20_000 }),
    onSettled: () => void qc.invalidateQueries({ queryKey: key('wallet-pass') }),
  });
}

export function useAccountOptions() {
  const { slug } = useTenant();
  return useQuery({ queryKey: ['p1', slug, 'account-options'], queryFn: () => apiFetch<AccountOptionsView>('/app/account/options', { anonymous: true }), staleTime: 10 * 60_000 });
}

export function useSpendToken() {
  return useMutation({
    mutationFn: (input: { pin?: string; otpChallengeId?: string; otpCode?: string }) => apiFetch<ScanTokenResponse>('/app/wallet/scan-token', { method: 'POST', body: input }),
  });
}

export function useSetPin() {
  return useMutation({
    mutationFn: (input: { pin: string; currentPin?: string; otpChallengeId?: string; otpCode?: string }) => apiFetch<{ ok: true }>('/app/me/pin', { method: 'POST', body: input }),
  });
}

export const accountApi = {
  recoverStart: (phone: string) => apiFetch<OtpStepView>('/app/recover/start', { method: 'POST', body: { phone }, anonymous: true }),
  recoverVerify: (challengeId: string, code: string) => apiFetch<SessionGrant>('/app/recover/verify', { method: 'POST', body: { challengeId, code }, anonymous: true }),
  claim: (code: string) => apiFetch<SessionGrant>('/app/claim', { method: 'POST', body: { code }, anonymous: true }),
  phoneVerifyStart: () => apiFetch<OtpStepView>('/app/me/phone/verify/start', { method: 'POST' }),
  phoneVerify: (challengeId: string, code: string) => apiFetch<{ verified: true }>('/app/me/phone/verify', { method: 'POST', body: { challengeId, code } }),
  stepUpStart: () => apiFetch<OtpStepView>('/app/me/step-up/start', { method: 'POST' }),
};
